// janus-engine/ability/orbs/orb-cast.ts — the browser twin of janus.php's MACHINIST ORBS block (Session 1 step 1, 2026-09-29):
// channel_and_evoke_orbs_on_cast() → ft_run_authored_channel_block() (includes/functions/janus/encounter/orb_queue.php).
//
// Once per cast, caster-side, BEFORE the ability's own dispatch (janus.php ~L1601, ahead of custom_abilities_roll / the templates /
// apply_persistent_aoe_to_encounter): consume_orb pays, then the three evoke shapes, then channel_orb × channel_orb_qty, the player's
// channel_chosen_orb picks (use_ability_variable_2, a slug CSV — padded at random), then channel_random_orb.
//
// MODELLED: consume, every channel shape, the slot cap, the orb_channeled log rows, the queue write. The random pick is a Janus die
// (ft_rng_die(count), the PHP seam since 2026-09-29), so the browser draws it and the replay oracle tapes it.
// EVOKES (lane A10, 2026-10-02 — R-026 plan F2; was the hand-back `orb_queue:evoke-dispatch`): evoke_orb_repeat / evoke_orb / evoke_all_orbs
// and a channel past the slot cap (the front orb force-evokes) pop the orb, log `orb_evoked` and fire its evoke ability through janus() in an
// `orb_evoke` trigger frame (ft_fire_orb_ability: targets_arr [] — the engine's enemy auto-pick / Self, enemy-autopick.ts), exactly in PHP's
// order (consume → repeat → counted → all (detached snapshot) → the channel block). The same fire serves the END-OF-TURN pulse pass
// (orbEndOfTurnPass — the End Turn tick, turns/ticks.ts).
// Spell/curse carriers run before their later counterspell/mana settlement. Delayed casts run only at their finish;
// nested orb refusals are silent, as in PHP's trigger frame. Unported nested dispatches still hand back pre-draw.
import { type JanusCtx, type Row, Refusal, ec, ecId, ecLean, handBack, insertRoll, itemField, record, upper } from '../context.js';
import { ORB_SLUGS as SLUGS, ORB_TYPES, normalizeOrbQueue, orbQueueText } from './orb-queue.js';
import { janus } from '../janus.js';
import { statusRows, statusItemId, statusPaused } from '../../statuses/expiry.js';
import { ftConsumeOrbType, ftEvokeAllOrbs, ftChannelRandomOrbCount, ftChannelChosenOrbCount } from '../../port/k4-dispatch/orb_queue.js';
import { browserDependencies } from '../../port/browser-dependencies.js';
import { nativePortValue } from '../../port/native-value.js';
import type { E, R } from '../../port/k4-dispatch/support-b2.js';
import { PhpArray } from '../../php-compat/index.js';

function orbPortEnvironment(ctx: JanusCtx, caster: Row, queue: ReturnType<typeof orbQueueOf>): E {
  return browserDependencies({
    layer_value: (row: Row, key: string) => ctx.read.value(row, key),
    ft_orb_type_row: (slug: string) => ORB_TYPES.find(orb => orb.slug === slug) ?? null,
    ft_orb_queue: () => queue.read(),
    ft_orb_queue_write: (_creature: R, value: unknown) => queue.write(value instanceof PhpArray ? [...value.values()] as string[] : value as string[]),
    ft_announce_and_fire_orb_evoke: (slug: string) => announceAndFireEvoke(ctx, caster, slug, 1),
  }) as unknown as E;
}

function randomOrb(ctx: JanusCtx): string {
  const drawn = ctx.roll.d(SLUGS.length);
  return SLUGS[(ctx.randomPickProbe?.(SLUGS.length, drawn) ?? drawn) - 1]!;
}

/** FT_ORB_QUEUE_DEFAULT_SLOTS / FT_ORB_QUEUE_MAX_SLOTS / FT_CHANNEL_ORB_QTY_MAX. */
const DEFAULT_SLOTS = 3;
const ORB_QUEUE_MAX_SLOTS = 10;
const CHANNEL_QTY_MAX = 20;
/** ClassMechanic 'orb_slots'. */
const ORB_SLOTS_ID = 'DADEF475-1E69-4EF4-8B46-06F3FC2B450D';
/** FT_FABRICATE_CASTABLE_ITEM_TYPES (fabricate_on_cast.php) — ft_orb_cast_should_dispatch()'s type list. */
const CASTABLE_TYPES = ['ability', 'full_ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability',
  'opportunity_attack_ability', 'lair_ability', 'menta', 'sanguine', 'potion', 'coating', 'food', 'drink', 'elixir', 'spell', 'curse'];
/** FT_FABRICATE_DELAYED_CASTING_TIMES. */
const DELAYED_CASTS = ['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours'];
export const ORB_LAYERS = ['consume_orb', 'channel_orb', 'channel_random_orb', 'channel_chosen_orb', 'evoke_orb', 'evoke_orb_repeat', 'evoke_all_orbs'] as const;

/** PHP (int) of a scalar layer value: ints truncate, numeric-leading strings parse, everything else is 0. */
function phpInt(value: unknown): number {
  let v = value;
  while (Array.isArray(v)) v = v[0];
  if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : 0;
  if (typeof v === 'boolean') return v ? 1 : 0;
  // a numeric-leading string, scientific notation included ('2e1' → 20, as PHP's (int) cast reads it — Codex A10 r1 P2)
  if (typeof v === 'string') { const m = /^\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/.exec(v); if (!m) return 0; const n = Math.trunc(Number(m[0])); return Number.isFinite(n) ? n : 0; }
  return 0;
}
const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));
function layerSlug(entry: unknown): string {
  return typeof entry === 'string' ? entry : String(record(entry)?.value ?? '');
}

/** Does this ability carry any orb layer (the block's own gate, janus.php ~L1602)? */
export function hasOrbLayer(ctx: JanusCtx, ability: Row): boolean {
  return ORB_LAYERS.some((key) => ctx.read.has(ability, key));
}

/** ft_orb_slot_max(): the Orb Slots pool's current charges, else its max, else 3 (each capped at 10). */
function orbSlotMax(ctx: JanusCtx, caster: Row): number {
  for (const entry of ctx.read.entries(caster, 'passive_ability')) {
    const row = record(entry);
    if (!row) continue;
    const id = upper(row.ability_id ?? itemField(record(row.item_arr), 'item_id') ?? itemField(row, 'item_id'));
    if (id !== ORB_SLOTS_ID) continue;
    // ability_entry_charges() / ability_entry_max_charges(): the compiled pair, the legacy flat keys, then the definition's layer
    const charges = record(row.charges);
    const layer = () => ctx.read.value(record(row.item_layer_types) ? row : (ctx.read.definition(id) ?? row), 'ability_charges');
    const current = phpInt(charges && 'current' in charges ? charges.current : 'ability_charges' in row ? row.ability_charges : layer());
    if (current > 0) return Math.min(ORB_QUEUE_MAX_SLOTS, current);
    const max = phpInt(charges && 'max' in charges ? charges.max : 'max_ability_charges' in row ? row.max_ability_charges : layer());
    if (max > 0) return Math.min(ORB_QUEUE_MAX_SLOTS, max);
  }
  return DEFAULT_SLOTS;
}

/** ft_resolve_chosen_orb_picks(): the untrusted CSV → exactly `count` catalog slugs, the unnamed rest drawn at random. */
function resolveChosenPicks(ctx: JanusCtx, rawPicks: unknown, count: number): string[] {
  const n = clamp(count, 0, CHANNEL_QTY_MAX);
  if (n === 0) return [];
  const picks: string[] = [];
  if (typeof rawPicks === 'string' && rawPicks !== '') {
    const longest = Math.max(...SLUGS.map((slug) => slug.length));
    // explode(',', substr($raw, 0, $max_bytes), FT_CHANNEL_ORB_QTY_MAX + 1): the LAST token keeps the unsplit remainder
    const parts = rawPicks.slice(0, CHANNEL_QTY_MAX * (longest + 1)).split(',');
    const tokens = parts.length > CHANNEL_QTY_MAX + 1 ? [...parts.slice(0, CHANNEL_QTY_MAX), parts.slice(CHANNEL_QTY_MAX).join(',')] : parts;
    for (const candidate of tokens) {
      const slug = candidate.trim().toLowerCase();
      if (slug !== '' && SLUGS.includes(slug)) picks.push(slug);
      if (picks.length >= n) break;
    }
  }
  while (picks.length < n) picks.push(randomOrb(ctx));
  return picks;
}

/**
 * channel_and_evoke_orbs_on_cast(): run once, just before the ability's own dispatch. `picks` = use_ability_variable_2.
 * A no-op for an ability with no orb layer (and for a non-castable item_type, exactly as ft_orb_cast_should_dispatch()).
 */
export function orbCastBlock(ctx: JanusCtx, ability: Row, caster: Row, picks: unknown, phase: unknown = null): void {
  if (!hasOrbLayer(ctx, ability)) return;
  const type = String(itemField(ability, 'item_type') ?? '');
  const triggeredPassive = type === 'passive_ability' && !!ctx.trigger;
  if (!CASTABLE_TYPES.includes(type) && !triggeredPassive) return;
  // PHP's block precedes template_spell_roll's counterspell and mana debit. A later counterspell
  // does not undo these orbs. Long casts channel only on the finish, never on their start.
  const key = String(phase ?? '');
  if (['damage_only', 'end_turn_in_p_aoe', 'moving_into_p_aoe', 'read', 'finish_reading', 'unread_book',
    'finish_training', 'arm_counterspell', 'finish_gathering', 'finish_lockpicking', 'finish_crafting_wip',
    'finish_disenchanting', 'throw_weapon'].includes(key)) return;
  const finish = ['finish_casting', 'finish_casting_ritual_spell'].includes(key);
  if (DELAYED_CASTS.includes(String(ctx.read.value(ability, 'spell_casting_time') ?? '')) !== finish) return;
  const queue = orbQueueOf(caster);
  const env = orbPortEnvironment(ctx, caster, queue);
  // consume_orb: remove the OLDEST matching slug, no evoke (the precondition gate already proved each is channeled)
  for (const entry of ctx.read.entries(ability, 'consume_orb')) {
    const slug = layerSlug(entry);
    if (slug === '' || !SLUGS.includes(slug)) continue;
    nativePortValue(ftConsumeOrbType({ value: caster }, slug, { value: [] }, env));
  }
  // THREE evoke shapes (channel_and_evoke_orbs_on_cast): evoke_orb_repeat R fires the FRONT orb's evoke R times and pops it once (Dualcast);
  // evoke_orb N pops N orbs, each firing once (a dry queue stops it); evoke_all_orbs pops EVERY orb — the snapshot (≤ 10) detached from
  // storage BEFORE any fires — each once, in queue order (Shatter). Still before the channel block: a fresh orb is never evoked by its cast.
  const repeat = phpInt(ctx.read.value(ability, 'evoke_orb_repeat'));
  if (repeat > 0) evokeFrontOrb(ctx, caster, queue, repeat);
  const count = phpInt(ctx.read.value(ability, 'evoke_orb'));
  for (let i = 0; i < count; i++) if (!evokeFrontOrb(ctx, caster, queue, 1)) break;
  if (phpInt(ctx.read.value(ability, 'evoke_all_orbs')) === 1 && queue.read().length) {
    nativePortValue(ftEvokeAllOrbs({ value: caster }, { value: [] }, '', { value: ctx.encounter }, null, env));
  }
  runAuthoredChannelBlock(ctx, ability, caster, queue, picks);
}

/** ft_run_authored_channel_block(): authored slugs × qty, chosen picks, then random. */
function runAuthoredChannelBlock(ctx: JanusCtx, ability: Row, caster: Row, queue: ReturnType<typeof orbQueueOf>, picks: unknown): void {
  const qty = clamp(phpInt(ctx.read.value(ability, 'channel_orb_qty') ?? 1), 1, CHANNEL_QTY_MAX);
  for (const entry of ctx.read.entries(ability, 'channel_orb')) {
    const slug = layerSlug(entry);
    if (slug === '') continue;
    for (let i = 0; i < qty; i++) channelOrb(ctx, caster, queue, slug);
  }
  const env = orbPortEnvironment(ctx, caster, queue);
  for (const slug of resolveChosenPicks(ctx, picks, ftChannelChosenOrbCount(ability, env))) channelOrb(ctx, caster, queue, slug);
  const random = ftChannelRandomOrbCount(ability, env);
  for (let i = 0; i < random; i++) channelOrb(ctx, caster, queue, randomOrb(ctx));
}

/** ft_orb_start_of_turn_channel_pass(): snapshot the bearer's live candidates, re-confirm before each replay. */
export function orbStartOfTurnChannelPass(ctx: JanusCtx, bearer: Row): void {
  const candidates = statusRows(bearer, 'target').filter(s => !statusPaused(s)).flatMap(status => {
    const definition = record(status.status_arr) ?? ctx.read.definition(statusItemId(status));
    return definition && phpInt(ctx.read.value(definition, 'channel_orb_each_turn')) === 1 ? [{ status, definition }] : [];
  });
  const queue = orbQueueOf(bearer);
  for (const { status, definition } of candidates) {
    const id = upper(status.status_id);
    if (status.tick_added && !ctx.statusesAdded.some(s => upper(s.target) === ecId(bearer)
        && upper(s.status_item_id) === statusItemId(status) && upper(s.status_connected_id) === upper(status.status_connected_id))) continue;
    if (id && !statusRows(bearer, 'target').some(s => upper(s.status_id) === id && !statusPaused(s)
        && !ctx.statusesRemoved.some(r => upper(r.target) === ecId(bearer) && upper(r.status_item_id) === statusItemId(s)))) continue;
    runAuthoredChannelBlock(ctx, definition, bearer, queue, null);
  }
}

/** The caster's queue as ft_orb_queue() reads it and ft_orb_queue_write() writes it: every write lands on the member row at once (the
 *  nested orb dispatches read the live row, as PHP's update_encounter_creature_stats syncs the in-memory array). ecChanges compares the
 *  column TEXT, so a queue that ends where it began makes no EC diff. */
function orbQueueOf(caster: Row): { read: () => string[]; write: (next: string[]) => void } {
  return { read: () => normalizeOrbQueue(ecLean(caster, 'orb_queue')) ?? [],
    write: (next) => { ec(caster).orb_queue = orbQueueText(next); } };
}

/** ft_channel_orb(): past the slot cap the FRONT orb force-evokes first (the StS rule), then the slug joins the tail and is logged. */
function channelOrb(ctx: JanusCtx, caster: Row, queue: ReturnType<typeof orbQueueOf>, slug: string): void {
  const orb = ORB_TYPES.find((o) => o.slug === slug);
  if (!orb) return;
  const slotMax = orbSlotMax(ctx, caster);
  if (slotMax < 1) return;
  while (queue.read().length >= slotMax) evokeFrontOrb(ctx, caster, queue, 1);
  queue.write([...queue.read(), slug]);
  if (orbFrameContext(ctx, caster)) insertRoll(ctx, { roll_type_1: 'orb_channeled', roll_item_id: orb.itemId, by: caster });
}

/** ft_evoke_front_orb(): pop index 0 (written before anything fires), announce it, fire its evoke `fireTimes`. False = the queue was empty. */
function evokeFrontOrb(ctx: JanusCtx, caster: Row, queue: ReturnType<typeof orbQueueOf>, fireTimes: number): boolean {
  const [front, ...rest] = queue.read();
  if (front === undefined) return false;
  queue.write(rest);
  announceAndFireEvoke(ctx, caster, front, fireTimes);
  return true;
}

/** ft_announce_and_fire_orb_evoke(): the `orb_evoked` row (only with full encounter context — the fire's own gate), then the evoke ability. */
function announceAndFireEvoke(ctx: JanusCtx, caster: Row, slug: string, fireTimes: number): void {
  const orb = ORB_TYPES.find((o) => o.slug === slug);
  if (!orb) return;
  if (orbFrameContext(ctx, caster)) insertRoll(ctx, { roll_type_1: 'orb_evoked', roll_item_id: orb.itemId, by: caster });
  for (let i = 0; i < Math.max(1, fireTimes); i++) fireOrbAbility(ctx, orb.evoke, caster, 'orb_evoke');
}

/** ft_fire_orb_ability()'s context gate: an encounter, and a caster that is a row of one (synthetic rows stay inert and unlogged). */
function orbFrameContext(ctx: JanusCtx, caster: Row): boolean {
  return !!ctx.encounter && /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(upper(ecLean(caster, 'encounter_id')));
}

/**
 * ft_fire_orb_ability(): one orb ability through janus() with the Machinist as caster and NO target (targeting is the ability's own — the
 * engine's enemy auto-pick or Self), in a trigger frame (`orb_passive` / `orb_evoke`: no AP / reaction debit, validation vetoes silent).
 * A refusal inside the frame is silent (PHP's frame swallows the same veto).
 */
export function fireOrbAbility(ctx: JanusCtx, abilityId: string | null, caster: Row, trigger: 'orb_passive' | 'orb_evoke'): void {
  if (!abilityId || !orbFrameContext(ctx, caster)) return;
  const ability = ctx.read.definition(abilityId);
  if (!ability) handBack(`orb_queue:orb-ability-definition-not-held:${abilityId}`);
  const prior = ctx.trigger, priorStart = ctx.dispatchRowStart;
  ctx.trigger = trigger;
  // each orb fire is its OWN janus() call in PHP: its per-dispatch accumulation (damage_roll's AoE roll_type_2 rule — a second Lightning pulse
  // is not the first pulse's area continuation) starts here. (A status an earlier fire added stays PENDING to the next — the caster's live
  // array carries it, as PHP's does: the orb-pass capture's natural-20 Fortunate is consumed by the next pulse's contest.)
  ctx.dispatchRowStart = ctx.rows.length;
  try {
    janus(ctx, { casterId: ecId(caster), targetIds: [], abilityId, rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null,
      useAbilityVariable2: null }, ability, caster, []);
  } catch (error) {
    // janus_trigger frames silently skip a veto. The popped/logged orb remains paid.
    if (error instanceof Refusal) return;
    throw error;
  } finally { ctx.trigger = prior; ctx.dispatchRowStart = priorStart; }
}

/** ft_orb_end_of_turn_pass() (end_turn_inc.php, after the Mastery upkeep): each channeled orb fires its PASSIVE, front to back; orbs stay. */
export function orbEndOfTurnPass(ctx: JanusCtx, caster: Row): void {
  for (const slug of normalizeOrbQueue(ecLean(caster, 'orb_queue')) ?? []) {
    const orb = ORB_TYPES.find((o) => o.slug === slug);
    if (orb) fireOrbAbility(ctx, orb.passive, caster, 'orb_passive');
  }
}

/** A use_ability_variable_2 that is ONLY this block's chosen-orb pick list (the orb modal's `cast(id, '', 'fire,cold')`) — no other
 * janus() consumer reads a lower-case slug CSV (the component / arcane-arrow / contingency markers carry underscores; the summon,
 * ammo and sheathed-weapon picks are UUIDs; crafting sizes ride a crafting roll). */
export function isChosenOrbPickVariable(ctx: JanusCtx, ability: Row, variable1: unknown, variable2: unknown): boolean {
  if (variable1 != null && variable1 !== '') return false;
  if (typeof variable2 !== 'string' || variable2.length > 512 || !/^[a-z ,]*$/.test(variable2)) return false;
  if (!ctx.read.has(ability, 'channel_chosen_orb')) return false;
  const type = String(itemField(ability, 'item_type') ?? '');
  return !['spell', 'curse'].includes(type);
}
